import { LinearProgress } from "@mui/material";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { GetSupportedFiletypes } from "../../app/slices/AssetsSlice";
import {
  LanguageData,
  selectAvailableLanguages,
} from "../../app/slices/TrainingLocalizationSlice";
import PiDropdown from "../../components/PiDropdown";
import { PiPopperMenuProps } from "../../components/PiPopper";
import { AssetKey, AssetVariantDTO } from "../../dto/AssetDTO";
import { ReactComponent as FileIcon } from "../file_explorer/assets/file.svg";
import "./LocalizeFile.css";
import { variantFileName, variantKind, variantLanguage } from "./variantDisplay";

interface VariantDetailsPaneProps {
  variant?: AssetVariantDTO;
  assetKey: AssetKey;
  /** Languages still free to assign, plus the one this variant already holds. */
  languageOptions: LanguageData[];
  /** Staged, not yet saved — the pane is a form until the footer commits it. */
  pendingLanguage?: LanguageData;
  onLanguageChange: (language: LanguageData) => void;
  pendingFilePreview?: string;
  onFileSelected: (file: File) => void;
  isSaving: boolean;
}

/** Same type tints the cards and rows use, at preview scale. */
const previewTint = (assetKey: AssetKey) =>
  assetKey === AssetKey.Pdf
    ? "assetcard-pdf"
    : assetKey === AssetKey.Video
      ? "assetcard-video"
      : assetKey === AssetKey.Audio
        ? "assetcard-audio"
        : "assetcard-loading-icon";

const PANE_CLASS =
  "w-[400px] shrink-0 border-l border-creator-border bg-creator-primary-background p-[16px] flex flex-col overflow-y-auto pit-quiet-scrollbar";

/**
 * The localize dialog's details column. It edits exactly one localization:
 * what the file looks like, which language it answers for, and how to swap
 * the file out. Assigning the language is the only thing that can be missing,
 * so it is the only thing this pane ever nags about.
 */
export default function VariantDetailsPane(props: VariantDetailsPaneProps) {
  const {
    variant,
    assetKey,
    languageOptions,
    pendingLanguage,
    onLanguageChange,
    pendingFilePreview,
    onFileSelected,
    isSaving,
  } = props;
  const { t } = useTranslation();
  const languages = useSelector(selectAvailableLanguages);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);

  if (!variant) return <div className={PANE_CLASS} />;

  const kind = variantKind(variant, languages);
  const isDefault = kind === "default";
  const language = pendingLanguage ?? variantLanguage(variant, languages);
  const fileName = variantFileName(variant);

  const title = isDefault
    ? t("default")
    : (language?.full ?? fileName ?? t("localize_ui.new_localization"));

  const supportedFileTypes = GetSupportedFiletypes(assetKey);
  const previewSrc = pendingFilePreview ?? variant.downloadUri;
  const showImage = assetKey === AssetKey.Image && previewSrc;

  const options: PiPopperMenuProps[] = languageOptions.map((lang) => ({
    text: lang.full,
    onClick: () => onLanguageChange(lang),
  }));

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileSelected(e.target.files[0]);
    }
  };

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === "dragenter" || e.type === "dragover");
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file && supportedFileTypes.includes(file.type)) onFileSelected(file);
  };

  return (
    <div className={PANE_CLASS} data-testid="variant-details-pane">
      <div className="text-[15px] font-semibold text-creator-text truncate">
        {title}
      </div>

      <div className="mt-[12px] h-[230px] shrink-0 rounded-[8px] border border-creator-border bg-creator-surface flex items-center justify-center overflow-hidden">
        {showImage ? (
          <img
            src={previewSrc}
            alt=""
            className="object-contain max-w-full max-h-full"
            data-testid="variant-preview-image"
          />
        ) : (
          <FileIcon
            className={previewTint(assetKey) + " w-[44px] h-[44px]"}
            data-testid="variant-preview-icon"
          />
        )}
      </div>

      {isSaving && (
        <div className="mt-[16px]">
          <LinearProgress />
        </div>
      )}

      {!isSaving && (
        <div className="mt-[18px]">
          <div className="text-[10px] font-semibold uppercase tracking-[0.08em] text-creator-text-sub mb-[6px]">
            {t("localize_ui.language")}
          </div>

          {isDefault ? (
            // The fallback file answers for every language that has no
            // localization, so there is nothing here to choose.
            <div className="h-[42px] px-[12px] rounded-6 border border-creator-border bg-creator-surface flex items-center text-[13px] text-creator-text-sub">
              {t("localize_ui.default_language_note")}
            </div>
          ) : (
            <>
              {/* Amber only while unassigned — an outline, never the text. */}
              <div
                className={
                  language
                    ? "pi-localize-language"
                    : "pi-localize-language pi-localize-language-unset"
                }
                data-testid="variant-language-dropdown"
              >
                <PiDropdown
                  width="100%"
                  options={options}
                  selectedText={
                    language?.full ?? t("localize_ui.choose_language")
                  }
                />
              </div>

              {!language && (
                <div className="mt-[8px] text-[11px] leading-[16px] text-creator-notice-text">
                  {t("localize_ui.unassigned_hint")}
                </div>
              )}
            </>
          )}

          <div
            className={
              "variantreplace-root mt-[14px] rounded-6 border border-dashed cursor-pointer text-center py-[11px] text-[12px] transition-colors " +
              (dragActive
                ? "variantreplace-dragactive border-creator-primary bg-creator-primary-washed text-creator-text-key"
                : "border-creator-border text-creator-text-key hover:bg-creator-primary-washed")
            }
            onClick={() => inputRef.current?.click()}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            data-testid="variant-replace-file"
          >
            {t("localize_ui.replace_file")}
          </div>

          {fileName && (
            <div className="mt-[10px] text-[11px] text-creator-text-sub truncate">
              {fileName}
            </div>
          )}

          <input
            className="filedropzone-hide-input"
            ref={inputRef}
            type="file"
            onChange={handleFileInput}
            accept={supportedFileTypes.toString()}
            data-testid="variant-replace-file-input"
          />
        </div>
      )}
    </div>
  );
}
